import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';
import { Text } from '../../ui/Text';
import { token, zVar } from '../../ui/theme';
import { useFullscreen } from '../../ui/useFullscreen';
import { getVolume, setVolume } from '../../ui/sounds';
import { V2apiClient } from '../../ApiClient';
import { useApiCall } from '../../useApiCall';

/*
 * GameSettings — a single gear button (top-right of the Stage) that opens a
 * settings panel. The panel gathers the table controls that used to live as
 * scattered corner buttons: sound volume, fullscreen, and leaving the game.
 * The host also gets a "lock table" toggle here (closes the table to new
 * players; admits spectators via the invite code).
 *
 * Leaving keeps its own confirm step: choosing "Leave game" swaps the settings
 * panel for a confirmation modal so a stray tap doesn't abandon the game.
 */

interface GameSettingsProps {
  /** The caller holds the host role — shows the lock-table control. */
  youAreCreator?: boolean;
  /** Whether the table is currently locked to new players. */
  isLocked?: boolean;
  /** The caller is spectating (no seat) — tunes the leave copy. */
  isSpectator?: boolean;
}

const GearIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const GameSettings: React.FC<GameSettingsProps> = ({
  youAreCreator = false,
  isLocked = false,
  isSpectator = false,
}) => {
  const navigate = useNavigate();
  const apiCall = useApiCall();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [volume, setVolumeState] = useState<number>(() => getVolume());

  const toggleLock = async () => {
    setLockBusy(true);
    await apiCall(V2apiClient.setTableLock(!isLocked));
    // The board re-fetches on the resulting broadcast; reflect the new state
    // then. Just clear the busy flag here.
    setLockBusy(false);
  };

  const onVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value) / 100;
    setVolume(v);
    setVolumeState(v);
  };

  const leave = async () => {
    setLeaving(true);
    if (await apiCall(V2apiClient.leaveGame())) {
      navigate('/lobby');
      return;
    }
    setLeaving(false);
    setConfirmLeaveOpen(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setSettingsOpen(true)}
        style={{
          position: 'absolute',
          right: 24,
          top: 8,
          zIndex: zVar('z-hud'),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
        aria-label="Settings"
      >
        <GearIcon />
        Settings
      </Button>

      <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} title="Settings">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {/* Sound volume */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Sound</Text>
              <Text variant="muted">{volume <= 0 ? 'Muted' : `${Math.round(volume * 100)}%`}</Text>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(volume * 100)}
              onChange={onVolumeChange}
              aria-label="Sound volume"
              style={{ width: '100%', accentColor: token('color-gold') }}
            />
          </div>

          {/* Fullscreen */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
            }}
          >
            <Text>Fullscreen</Text>
            <Button variant="secondary" size="sm" onClick={toggleFullscreen}>
              {isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            </Button>
          </div>

          {/* Lock table (host only) */}
          {youAreCreator && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                borderTop: `1px solid ${token('color-border')}`,
                paddingTop: 'var(--space-4)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                }}
              >
                <Text>Lock table</Text>
                <Button
                  variant={isLocked ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={toggleLock}
                  disabled={lockBusy}
                >
                  {isLocked ? 'Locked' : 'Unlocked'}
                </Button>
              </div>
              <Text variant="muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                {isLocked
                  ? 'Hidden from the lobby. New people who open the invite link watch as spectators.'
                  : 'Open: anyone with the invite link can take an empty seat, and the table is listed in the lobby.'}
              </Text>
            </div>
          )}

          {/* Leave game */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              borderTop: `1px solid ${token('color-border')}`,
              paddingTop: 'var(--space-4)',
            }}
          >
            <Text>{isSpectator ? 'Stop watching' : 'Leave game'}</Text>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSettingsOpen(false);
                setConfirmLeaveOpen(true);
              }}
            >
              Leave
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmLeaveOpen}
        onClose={leaving ? undefined : () => setConfirmLeaveOpen(false)}
        title={isSpectator ? 'Stop watching?' : 'Leave the game?'}
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmLeaveOpen(false)} disabled={leaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={leave} disabled={leaving}>
              {leaving ? 'Leaving…' : isSpectator ? 'Stop watching' : 'Leave game'}
            </Button>
          </>
        }
      >
        <Text variant="muted">
          {isSpectator
            ? "You'll return to the lobby. The game continues without interruption."
            : "You'll return to the lobby. If the game is in progress, it will pause so the host can replace you with a computer or wait for a new player to take your seat."}
        </Text>
      </Modal>
    </>
  );
};

export default GameSettings;
